import type { TerminalEvent } from '@developerz/domain';
import { batch, createMemo, createSignal, type JSX, Show, untrack } from 'solid-js';
import { ErrorState } from '../components/ErrorState';
import { isAnsweredRefusal } from '../components/RunStreamRefusal';
import { CommandLine, type CommandLinePaneIntent } from '../components/terminal/CommandLine';
import { TerminalPane } from '../components/terminal/TerminalPane';
import { TerminalPaneTree } from '../components/terminal/TerminalPaneTree';
import { TerminalStatusLine } from '../components/terminal/TerminalStatusLine';
import { TerminalTitlebar } from '../components/terminal/TerminalTitlebar';
import { t } from '../i18n';
import { activeAccountId } from '../lib/active-account';
import { urlOrg } from '../lib/org';
import { usePagedQuery } from '../lib/paged';
import { SESSION_TOKEN, useMe } from '../lib/session';
import type { StreamStatus } from '../lib/sseFrames';
import { TERMINAL_BUFFER_CAPACITY, TerminalRingBuffer } from '../lib/terminal/buffer';
import { nextPaneId, paneIds } from '../lib/terminal/layout';
import { type TerminalPanes, useTerminalPanes } from '../lib/terminal/use-terminal-panes';
import { type TerminalEventRow, useTerminalEvents } from '../lib/terminal-events';
import { TERMINAL_STATUS_LINE_POLL_MS, useTerminalStatus } from '../lib/terminal-status';
import { useTerminalStream, useTerminalSystemLines } from '../lib/terminal-stream';

// The org's live console (#4395/#4396/#4397, epic #4391): window chrome, a
// status line, and a PANE TREE — every pane a filtered view over the SAME
// shared buffer, never a second query and never a second connection
// (`lib/sse-one-seam.test.ts` pins the one live caller to THIS file).
// CONTAINER — data + layout (`use-terminal-panes.ts`) + per-pane filter
// state; the pane/tree/titlebar components own rendering.
//
// `token` is injectable for tests, mirroring every other route this app mounts
// with a `token?` prop (`Ci`, `AccountAudit`).

/**
 * The command line's `onIntent` wiring seam (#4398), pulled out of `Terminal`
 * itself so that function stays under `scripts/size-budget.ts`'s 120-line
 * ceiling: every pane intent targets `focusedId`, read fresh by the caller at
 * dispatch time (see the render below). `follow`/`pause`/`tail` move the
 * SAME per-pane flag `TerminalLog`'s scroll and pill use; `clear` records the
 * newest row held now, and that pane shows only what lands after it — the
 * shared buffer (and every other pane's view of it) is untouched.
 */
function dispatchCommandLineIntent(
  panes: TerminalPanes,
  focusedId: string,
  rows: () => readonly TerminalEvent[],
  intent: CommandLinePaneIntent,
): void {
  switch (intent.kind) {
    case 'split':
      if (intent.axis === 'row') panes.onSplitRow(focusedId);
      else panes.onSplitColumn(focusedId);
      return;
    case 'zoom':
      panes.onToggleZoom(focusedId);
      return;
    case 'close':
      panes.onClose(focusedId);
      return;
    case 'filter':
      panes.setFilterText(focusedId, intent.text);
      return;
    case 'tail':
      if (intent.text !== '') panes.setFilterText(focusedId, intent.text);
      panes.setFollow(focusedId, true);
      return;
    case 'follow':
    case 'pause':
      panes.setFollow(focusedId, intent.kind === 'follow');
      return;
    case 'clear': {
      const last = rows()[rows().length - 1];
      panes.setClearedAt(focusedId, last === undefined ? null : { id: last.id, ts: last.ts });
    }
  }
}

const STREAM_STATUS_LABEL: Readonly<Record<StreamStatus, string>> = {
  connecting: 'terminal.command_status_connecting',
  live: 'terminal.command_status_live',
  done: 'terminal.command_status_done',
  error: 'terminal.command_status_error',
};

/** The `status` verb's one line: the focused pane and the one shared connection. */
function describeFocusedPane(
  panes: TerminalPanes,
  focusedId: string,
  stream: StreamStatus,
): string {
  const ids = paneIds(panes.layout().root);
  const onOff = (on: boolean): string =>
    on ? t('terminal.command_status_on') : t('terminal.command_status_off');
  const filter = panes.filterTextFor(focusedId).trim();
  return t('terminal.command_status', {
    pane: `${ids.indexOf(focusedId) + 1}/${ids.length}`,
    stream: t(STREAM_STATUS_LABEL[stream]),
    filter: filter === '' ? t('terminal.command_status_filter_none') : filter,
    verbose: onOff(panes.verboseFor(focusedId)),
    follow: onOff(panes.followFor(focusedId)),
  });
}

/**
 * ONE ring buffer for the window's lifetime — capacity-bounded so repeated
 * "load earlier" clicks cannot grow the DOM without limit. The live tail
 * (#4396) and the boot/connection system lines merge into this SAME buffer,
 * in `(ts, id)` order alongside the backfill — one accumulation, shared by
 * EVERY pane (#4397): a pane is a FILTER over `rows()`, never its own fetch.
 * Pulled out of `Terminal` itself (#4398) only to keep that function under
 * `scripts/size-budget.ts`'s 120-line ceiling — the mechanism is unchanged.
 *
 * CLEARED ON AN ACCOUNT SWITCH — the same boundary `usePagedQuery` itself
 * enforces (`lib/paged.ts`'s "THE ACCOUNT BOUNDARY"). An org switch NAVIGATES
 * rather than remounting the route, so `buffer` survives it; a purely-
 * additive `merge()` with no clear folds org B's rows in alongside org A's, a
 * cross-tenant bleed. `heldFor` mirrors `paged.ts`'s own field: the account
 * this memo is CURRENTLY caught up to. A batch read in the SAME pass the
 * switch is detected is evidence about the tenant just left, not the new
 * one, so it is never merged — every batch is still touched (via `.length`)
 * to keep the memo subscribed to whatever lands next, once the new tenant's
 * own first page (and its own fresh live tail / boot sequence, both
 * `useTerminalStream`'s and `useTerminalSystemLines`'s own concern) actually
 * arrives.
 */
function useTerminalRows(
  batchRows: () => readonly TerminalEventRow[],
  liveEvents: () => readonly TerminalEventRow[],
  systemLines: () => readonly TerminalEventRow[],
): () => readonly TerminalEvent[] {
  let heldFor = untrack(activeAccountId);
  const buffer = new TerminalRingBuffer<TerminalEventRow>(TERMINAL_BUFFER_CAPACITY);
  return createMemo((): readonly TerminalEvent[] => {
    const account = activeAccountId();
    const batch = batchRows();
    const liveBatch = liveEvents();
    const sysBatch = systemLines();
    if (account !== heldFor) {
      heldFor = account;
      buffer.clear();
      void batch.length; // keep every subscription alive for the next pass
      void liveBatch.length;
      void sysBatch.length;
      return buffer.all();
    }
    buffer.merge(batch);
    buffer.merge(liveBatch);
    buffer.merge(sysBatch);
    return buffer.all();
  });
}

/**
 * The backfill, widened with the live tail's `?verbose=` (plan 101 slice 17 row
 * 17-20): a verbose pane after a reload otherwise held run events from connect
 * time onward and none before. A flip of `verbose` is a different listing, so
 * `relist` drops the cursor held from the old one in the SAME batch as the flip:
 * the query re-keys straight to page one, never to (new listing, old cursor).
 * Rows already shown stay — the ring buffer holds them, deduped by id. Every
 * write that can move `verbose` goes through `relist`: a pane's toggle, its
 * close button, and every command-line intent (`close` ends a pane too).
 */
function useTerminalBackfill(token: string | null, verbose: () => boolean) {
  const list = usePagedQuery<TerminalEventRow>((cursor) =>
    useTerminalEvents(cursor, token, verbose),
  );
  const relist = (change: () => void): void =>
    batch(() => {
      const was = verbose();
      change();
      if (verbose() !== was) list.reset();
    });
  return { list, relist };
}

export function Terminal(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const hasSession = token !== null;
  const org = (): string => urlOrg() ?? '';

  const status = useTerminalStatus(token, TERMINAL_STATUS_LINE_POLL_MS);
  const panes = useTerminalPanes();
  const focusedId = panes.focusedId;
  const { list, relist } = useTerminalBackfill(token, panes.anyVerbose);
  // `panes.anyVerbose` (#4399 slice 08): the OR of every pane's own toggle —
  // there is exactly one live connection for the whole window, so that is
  // what the shared stream's own `?verbose=` reflects. See
  // `use-terminal-panes.ts`'s `anyVerbose` docblock and `terminal-stream.ts`'s
  // `useTerminalStream` for the redial-on-change wiring.
  const stream = useTerminalStream(token, panes.anyVerbose);
  const me = useMe(token);
  // Bumped by any pane's title-bar "Command line" button to move focus to
  // the ONE command line below — see `CommandLine.tsx`'s own docblock for why
  // a bump signal, not a ref, is the seam between them.
  const [commandLineFocusRequest, setCommandLineFocusRequest] = createSignal(0);

  // "authenticated as <login>" (boot milestone 1) proves identity off the
  // SAME call every other view already makes — `useMe`'s query is cached app-
  // wide, so this is not a second request. `github_login` (always present)
  // is the fallback for a PAT session, which `user` is null for.
  const login = (): string | null => me.data?.user?.login ?? me.data?.github_login ?? null;
  const workers = (): { readonly online: number; readonly total: number } | null => {
    const data = status.data;
    return data === undefined ? null : { online: data.workers.online, total: data.workers.total };
  };
  const systemLines = useTerminalSystemLines({
    login,
    org,
    account: activeAccountId,
    workers,
    streamStatus: stream.status,
  });

  // See `useTerminalRows`'s own docblock for the buffer/account-switch mechanism.
  const rows = useTerminalRows(list.rows, stream.events, systemLines);

  // Bare accessors (`rows`, not `rows()`) — see `TerminalPaneTree.tsx`.
  function renderPane(id: string): JSX.Element {
    return (
      <TerminalPane
        id={id}
        label={() => panes.paneLabel(id)}
        rows={rows}
        filterText={() => panes.filterTextFor(id)}
        onFilterTextChange={(text) => panes.setFilterText(id, text)}
        kindFilter={() => panes.kindFilterFor(id)}
        focused={() => panes.layout().focusId === id}
        live={() => stream.status() === 'live'}
        zoomed={() => panes.layout().zoomId === id}
        verbose={() => panes.verboseFor(id)}
        follow={() => panes.followFor(id)}
        onFollowChange={(on) => panes.setFollow(id, on)}
        clearedAt={() => panes.clearedAtFor(id)}
        timeMode={panes.timeMode}
        searchRequest={() => panes.searchRequestFor(id)}
        onOpenSearch={() => panes.requestSearch(id)}
        canClose={panes.canClose}
        onFocus={() => panes.onFocus(id)}
        onSplitRow={() => panes.onSplitRow(id)}
        onSplitColumn={() => panes.onSplitColumn(id)}
        onToggleZoom={() => panes.onToggleZoom(id)}
        onToggleVerbose={() => relist(() => panes.setVerbose(id, !panes.verboseFor(id)))}
        onClose={() => relist(() => panes.onClose(id))}
        onOpenCommandLine={() => setCommandLineFocusRequest((n) => n + 1)}
        isLoading={() => list.view.isLoading}
        isError={() => list.view.isError}
        error={() => list.view.error}
        hasMoreBackfill={list.hasMore}
        isFetchingBackfill={list.isFetching}
        onLoadOlder={() => list.loadMore()}
        onRetry={() => void list.view.refetch()}
      />
    );
  }

  // A FAULT the banner must report vs an ANSWERED refusal (403/404) it must
  // not — see the `<Show>` below for the full reasoning. `refusal() === null`
  // covers a genuine drop (no HTTP status was ever answered) and reads as a
  // fault, exactly like `AuditSession.tsx`/`RunTimelinePanel.tsx`'s own
  // `refusal() !== null && isAnsweredRefusal(refusal())` gate.
  const streamFault = createMemo(() => {
    const refusal = stream.refusal();
    return stream.status() === 'error' && !(refusal !== null && isAnsweredRefusal(refusal));
  });

  return (
    <div class="terminal">
      <TerminalTitlebar
        org={org()}
        timeMode={panes.timeMode()}
        onToggleTimeMode={() =>
          panes.setTimeMode(panes.timeMode() === 'relative' ? 'clock' : 'relative')
        }
      />
      <Show when={hasSession} fallback={<p class="terminal__empty">{t('terminal.no_session')}</p>}>
        {/* The status line and the command line both act on whichever pane is
            FOCUSED, read fresh at each use, and neither remounts on a focus
            switch (plan 101 slice 19: row 19-14 and suspected 19-S3). The
            pressed cell is the pane's own (`statusCellFor`), and a half-typed
            command and the input's focus survive another pane taking focus. */}
        <TerminalStatusLine
          status={status.data}
          error={status.error}
          activeCell={panes.statusCellFor(focusedId())}
          onToggle={(cell) => panes.toggleStatusCell(focusedId(), cell)}
        />
        <CommandLine
          focusRequest={commandLineFocusRequest}
          onIntent={(intent) =>
            // Through `relist`: `close` can end the last verbose pane, which
            // flips the listing exactly as the pane's own toggle does.
            relist(() => dispatchCommandLineIntent(panes, focusedId(), rows, intent))
          }
          describeStatus={() => describeFocusedPane(panes, focusedId(), stream.status())}
          onCyclePane={(dir) => panes.onFocus(nextPaneId(panes.layout(), focusedId(), dir))}
          onSearch={() => panes.requestSearch(focusedId())}
        />
        {/* An ANSWERED refusal (403/404) is not a fault Retry can fix — the
            backfill query hits the SAME account-scoped fence and its own
            `isForbidden` branch (`TerminalLog.tsx`) already reports it via
            `<DeniedState/>`. Showing "connection lost" + Retry on top of that
            would be a second, misleading verdict about a refusal that isn't
            going anywhere. `RunStreamRefusal.tsx`'s own `isAnsweredRefusal`
            draws the identical line for the session/fleet-run append logs —
            reused via `streamFault` above, which is deliberately null-safe:
            coercing a null refusal to `{ kind: 'no_session' }` (a prior
            version of this fix) made `isAnsweredRefusal` return true for a
            GENUINE drop too, silently swallowing the banner AND the only
            `stream.retry()` on the page for it (#4396 review, caught before
            merge). */}
        <Show when={streamFault()}>
          {/* `stream.retry()` drops the resume token (the server restarts at
              now()), so the outage's rows come from the backfill's PAGE ONE —
              `reload()`, never `view.refetch()`, which after "Load earlier
              lines" re-reads page two (plan 101 slice 17 row 17-6). The buffer
              dedupes by id, so the re-merge cannot duplicate a row. */}
          <ErrorState
            message={t('terminal.stream_disconnected')}
            onRetry={() => {
              stream.retry();
              list.reload();
            }}
          />
        </Show>
        <TerminalPaneTree
          root={panes.layout().root}
          zoomId={panes.layout().zoomId}
          onRatioChange={panes.onRatioChange}
          renderPane={renderPane}
        />
      </Show>
    </div>
  );
}
